import { useCallback, useEffect, useRef, useState } from 'react'
import { useCampaignTotpStepUp } from '@/contexts/CampaignTotpStepUpContext'
import { fetchOperatingCostPaymentFeePostings } from '@/lib/api/operating-costs-admin.service'
import type { OperatingCostPaymentFeePostingsPage } from '@/types/api'
import {
  OPERATING_COSTS_LOAD_ERROR,
  resolveOperatingCostPaymentFeePostingsPage,
  resolveOperatingCostsError,
} from '../lib/operating-costs'

function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') return true
  if (error instanceof Error && (error.name === 'AbortError' || /abort/i.test(error.message))) {
    return true
  }
  return false
}

export type PaymentFeeFilterValues = {
  campaignId: string
  institutionId: string
  month: string
  from: string
  to: string
}

const EMPTY_FILTERS: PaymentFeeFilterValues = {
  campaignId: '',
  institutionId: '',
  month: '',
  from: '',
  to: '',
}

const EMPTY_PAGE: OperatingCostPaymentFeePostingsPage = {
  filtered_total: 0,
  postings: [],
  meta: {
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0,
  },
  filter_options: {
    campaigns: [],
    institutions: [],
  },
}

export type PaymentFeePostingsController = {
  data: OperatingCostPaymentFeePostingsPage | null
  isLoading: boolean
  error: string | null
  draftFilters: PaymentFeeFilterValues
  setDraftFilters: (filters: PaymentFeeFilterValues) => void
  applyFilters: () => void
  clearFilters: () => void
  goToPage: (page: number) => void
}

export function usePaymentFeePostings(
  categoryId: number,
  reloadToken = 0
): PaymentFeePostingsController {
  const { runWithStepUp } = useCampaignTotpStepUp()
  const runWithStepUpRef = useRef(runWithStepUp)
  runWithStepUpRef.current = runWithStepUp
  const [data, setData] = useState<OperatingCostPaymentFeePostingsPage | null>(
    categoryId < 1 ? EMPTY_PAGE : null
  )
  const [isLoading, setIsLoading] = useState(categoryId >= 1)
  const [error, setError] = useState<string | null>(null)
  const [draftFilters, setDraftFilters] = useState<PaymentFeeFilterValues>(EMPTY_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState<PaymentFeeFilterValues>(EMPTY_FILTERS)
  const [page, setPage] = useState(1)
  const loadControllerRef = useRef<AbortController | null>(null)

  const load = useCallback(
    async (filters: PaymentFeeFilterValues, pageNumber: number) => {
      if (categoryId < 1) {
        setData(EMPTY_PAGE)
        setIsLoading(false)
        setError(null)
        return
      }

      loadControllerRef.current?.abort()
      const controller = new AbortController()
      loadControllerRef.current = controller
      setIsLoading(true)
      setError(null)
      try {
        const campaignId = Number(filters.campaignId)
        const institutionId = Number(filters.institutionId)
        const response = await runWithStepUpRef.current(() =>
          fetchOperatingCostPaymentFeePostings(
            categoryId,
            {
              campaignId: Number.isInteger(campaignId) && campaignId > 0 ? campaignId : null,
              institutionId: Number.isInteger(institutionId) && institutionId > 0 ? institutionId : null,
              month: filters.month,
              from: filters.from,
              to: filters.to,
              page: pageNumber,
            },
            controller.signal
          )
        )
        if (controller.signal.aborted) return
        const resolved = resolveOperatingCostPaymentFeePostingsPage(response)
        if (!resolved) {
          setData(null)
          setError(OPERATING_COSTS_LOAD_ERROR)
          return
        }
        setData(resolved)
      } catch (err) {
        if (controller.signal.aborted || isAbortError(err)) return
        setData(null)
        setError(resolveOperatingCostsError(err))
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      }
    },
    [categoryId, reloadToken]
  )

  useEffect(() => {
    void load(appliedFilters, page)
    return () => {
      loadControllerRef.current?.abort()
    }
  }, [appliedFilters, load, page])

  const applyFilters = useCallback(() => {
    setPage(1)
    setAppliedFilters(draftFilters)
  }, [draftFilters])

  const clearFilters = useCallback(() => {
    setDraftFilters(EMPTY_FILTERS)
    setAppliedFilters(EMPTY_FILTERS)
    setPage(1)
  }, [])

  const goToPage = useCallback((nextPage: number) => {
    if (nextPage < 1) return
    setPage(nextPage)
  }, [])

  return {
    data,
    isLoading,
    error,
    draftFilters,
    setDraftFilters,
    applyFilters,
    clearFilters,
    goToPage,
  }
}
